/******************************************************************
 * ##: useLocationTracking hook
 * Subscribes to the shared location tracker status and exposes the number of
 * location records still pending synchronization, for the discrete GPS indicator.
 *
 * TL;DR: React state mirror of the GPS tracker status + pending count.
 * History:
 * 23-07-2026: Created
 ******************************************************************/

import { useCallback, useEffect, useState } from "react";
import { locationTracker } from "utils/location/geolocation";
import { getUnsyncedLocationRecords } from "utils/offlineCache";

const PENDING_REFRESH_INTERVAL_MS = 10000;

const useLocationTracking = () => {
  const [status, setStatus] = useState(locationTracker.getStatus());
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const unsubscribe = locationTracker.subscribe(setStatus);
    return unsubscribe;
  }, []);

  const refreshPendingCount = useCallback(async () => {
    const response = await getUnsyncedLocationRecords();
    if (response && response.status) {
      setPendingCount((response.data || []).length);
    }
  }, []);

  useEffect(() => {
    refreshPendingCount();
    const intervalId = setInterval(refreshPendingCount, PENDING_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [refreshPendingCount]);

  return { status, pendingCount, refreshPendingCount };
};

export default useLocationTracking;
